import React, { useState, useEffect } from "react";
import { X, Plus, Calendar as CalendarIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { COUNTIES } from "@/data/sampleListings";
import { POST_MODAL } from "@/constants/testIds";
import { parseNumericPrice, formatDateShort } from "@/lib/format";
import { toast } from "sonner";

const emptyState = {
  address: "",
  city: "",
  county: "Palm Beach",
  listPrice: "",
  daysOnMarket: "",
  availableDates: [],   // array of ISO YYYY-MM-DD strings
  dateDraft: "",        // input value for the date picker
  sellerPrefTime: "",
  notes: "",
  agentName: "",
  agentPhone: "",
  agentEmail: "",
  ackNoReferral: false,
  ackMarketing: false,
  notifyOnReport: false,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PostListingModal = ({ open, onOpenChange, onSubmit, prefill }) => {
  const [form, setForm] = useState(emptyState);
  const [submitting, setSubmitting] = useState(false);

  // When the modal is opened with a `prefill` object (from Duplicate), hydrate the form.
  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setForm({
        ...emptyState,
        address: prefill.address || "",
        city: prefill.city || "",
        county: prefill.county || "Palm Beach",
        listPrice: prefill.listPrice ? String(prefill.listPrice) : "",
        daysOnMarket: prefill.daysOnMarket != null ? String(prefill.daysOnMarket) : "",
        sellerPrefTime: prefill.sellerPrefTime || "",
        notes: prefill.notes || "",
        agentName: prefill.agentName || "",
        agentPhone: prefill.agentPhone || "",
        agentEmail: prefill.agentEmail || "",
      });
    } else {
      setForm(emptyState);
    }
  }, [open, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addDate = () => {
    const d = form.dateDraft;
    if (!d) return;
    if (form.availableDates.includes(d)) {
      toast.info("That date is already added");
      return;
    }
    setForm((f) => ({
      ...f,
      availableDates: [...f.availableDates, d].sort(),
      dateDraft: "",
    }));
  };

  const removeDate = (d) => {
    setForm((f) => ({ ...f, availableDates: f.availableDates.filter((x) => x !== d) }));
  };

  const canSubmit =
    form.address.trim() &&
    form.city.trim() &&
    form.county &&
    form.listPrice &&
    form.daysOnMarket !== "" &&
    form.availableDates.length > 0 &&
    form.agentName.trim() &&
    form.agentPhone.trim() &&
    form.agentEmail.trim() &&
    form.ackNoReferral &&
    form.ackMarketing;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    const basePayload = {
      address: form.address.trim(),
      city: form.city.trim(),
      county: form.county,
      listPrice: parseNumericPrice(form.listPrice),
      daysOnMarket: Number(form.daysOnMarket) || 0,
      sellerPrefTime: form.sellerPrefTime.trim(),
      notes: form.notes.trim(),
      agentName: form.agentName.trim(),
      agentPhone: form.agentPhone.trim(),
      agentEmail: form.agentEmail.trim(),
    };

    try {
      // Fan out — one listing per selected date. If any single call fails,
      // subsequent ones are aborted so the user knows a partial batch happened.
      let created = 0;
      for (const date of form.availableDates) {
        await onSubmit({ ...basePayload, availableDate: date });
        created += 1;
      }

      // Register listing-agent opt-in once (property-level, not per-date).
      if (form.notifyOnReport) {
        try {
          await fetch(`${API}/listing-optin`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              address: basePayload.address,
              city: basePayload.city,
              county: basePayload.county,
              agent_name: basePayload.agentName,
              agent_email: basePayload.agentEmail,
            }),
          });
        } catch (err) {
          console.error("listing-optin failed:", err?.message || err);
        }
      }

      toast.success(
        created === 1
          ? "Open house opportunity posted"
          : `Posted ${created} open house opportunities`,
        {
          description: form.notifyOnReport
            ? `${basePayload.address}, ${basePayload.city} · You'll be emailed each self-report`
            : `${basePayload.address}, ${basePayload.city}`,
        }
      );
      setForm(emptyState);
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to post listing(s):", err?.message || err);
      toast.error("Couldn't post opportunity", {
        description: "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={POST_MODAL.root}
        className="max-w-3xl p-0 gap-0 max-h-[92vh] overflow-hidden bg-white border-0 rounded-3xl shadow-2xl [&>button]:hidden"
      >
        {/* KW red top bar */}
        <div className="kw-red-bar h-1 w-full" />

        <DialogHeader className="px-8 py-5 border-b border-[color:var(--kw-border)] flex-row items-center justify-between space-y-0">
          <DialogTitle className="font-display text-2xl md:text-3xl font-black tracking-tight text-[color:var(--kw-charcoal)] uppercase">
            Post Open House Opportunity
          </DialogTitle>
          <DialogClose
            data-testid={POST_MODAL.closeBtn}
            className="text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)] transition-colors"
          >
            <X size={22} strokeWidth={2.5} />
          </DialogClose>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto kw-scroll px-8 py-6 space-y-5"
          style={{ maxHeight: "calc(92vh - 80px)" }}
        >
          {/* Property Address */}
          <div>
            <label className="label-caps block mb-1.5">Property Address *</label>
            <input
              data-testid={POST_MODAL.fieldAddress}
              className="input-kw"
              placeholder="e.g. 742 Beachfront Dr"
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              required
            />
          </div>

          {/* City + County */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-caps block mb-1.5">City *</label>
              <input
                data-testid={POST_MODAL.fieldCity}
                className="input-kw"
                placeholder="Wellington"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label-caps block mb-1.5">County Location *</label>
              <select
                data-testid={POST_MODAL.fieldCounty}
                className="input-kw appearance-none bg-white pr-8"
                value={form.county}
                onChange={(e) => set("county", e.target.value)}
              >
                {COUNTIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Price + DOM */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-caps block mb-1.5">List Price *</label>
              <input
                data-testid={POST_MODAL.fieldPrice}
                className="input-kw"
                placeholder="$500,000"
                value={form.listPrice}
                onChange={(e) => set("listPrice", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Days on Market *</label>
              <input
                data-testid={POST_MODAL.fieldDom}
                type="number"
                min="0"
                className="input-kw"
                placeholder="14"
                value={form.daysOnMarket}
                onChange={(e) => set("daysOnMarket", e.target.value)}
                required
              />
            </div>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-caps block mb-1.5">Available Date(s) *</label>
              <div className="flex gap-2">
                <input
                  data-testid={POST_MODAL.fieldDate}
                  type="date"
                  className="input-kw flex-1"
                  value={form.dateDraft}
                  onChange={(e) => set("dateDraft", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addDate();
                    }
                  }}
                />
                <button
                  type="button"
                  data-testid={POST_MODAL.fieldAddDate}
                  onClick={addDate}
                  disabled={!form.dateDraft}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[color:var(--kw-border)] text-xs font-black tracking-widest uppercase transition-colors ${form.dateDraft ? "text-[color:var(--kw-red)] hover:bg-[color:var(--kw-red)]/5 border-[color:var(--kw-red)]/30" : "text-[color:var(--muted-foreground)] cursor-not-allowed"}`}
                >
                  <Plus size={14} strokeWidth={2.5} />
                  Add
                </button>
              </div>
              {form.availableDates.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.availableDates.map((d, i) => (
                    <span
                      key={d}
                      data-testid={POST_MODAL.dateChip(i)}
                      className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-[color:var(--kw-cream)] border border-[color:var(--kw-border)] text-xs font-semibold text-[color:var(--kw-charcoal)]"
                    >
                      <CalendarIcon size={11} className="text-[color:var(--kw-red)]" strokeWidth={2.5} />
                      {formatDateShort(d)}
                      <button
                        type="button"
                        data-testid={POST_MODAL.removeDate(i)}
                        onClick={() => removeDate(d)}
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[color:var(--muted-foreground)] hover:bg-[color:var(--kw-red)]/10 hover:text-[color:var(--kw-red)]"
                        aria-label={`Remove ${d}`}
                      >
                        <X size={11} strokeWidth={2.5} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-[color:var(--muted-foreground)] mt-1.5 leading-snug">
                Pick a date and click <span className="font-bold">Add</span> to include more days for the same property.
              </p>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Seller&rsquo;s Pref. Time</label>
              <input
                data-testid={POST_MODAL.fieldTime}
                className="input-kw"
                placeholder="e.g. 12:00 PM - 3:00 PM"
                value={form.sellerPrefTime}
                onChange={(e) => set("sellerPrefTime", e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="label-caps block mb-1.5">Additional Notes</label>
            <textarea
              data-testid={POST_MODAL.fieldNotes}
              className="input-kw min-h-[90px] resize-y"
              placeholder="e.g. Bring water, long driveway, dogs in backyard..."
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </div>

          {/* Section: Listing Agent */}
          <div className="pt-3">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-[color:var(--kw-red)] rounded-full" />
              <h3 className="font-display text-lg font-black uppercase tracking-wider text-[color:var(--kw-red)]">
                Listing Agent Information
              </h3>
            </div>

            <div className="space-y-5">
              <div>
                <label className="label-caps block mb-1.5">Full Name *</label>
                <input
                  data-testid={POST_MODAL.fieldAgentName}
                  className="input-kw"
                  placeholder="Sarah Jenkins"
                  value={form.agentName}
                  onChange={(e) => set("agentName", e.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="label-caps block mb-1.5">Phone Number *</label>
                  <input
                    data-testid={POST_MODAL.fieldAgentPhone}
                    type="tel"
                    className="input-kw"
                    placeholder="561-555-0123"
                    value={form.agentPhone}
                    onChange={(e) => set("agentPhone", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Email Address *</label>
                  <input
                    data-testid={POST_MODAL.fieldAgentEmail}
                    type="email"
                    className="input-kw"
                    placeholder="sarah@kwwellington.com"
                    value={form.agentEmail}
                    onChange={(e) => set("agentEmail", e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Required Acknowledgments */}
          <div className="pt-2 divide-y divide-[color:var(--kw-border)]/60">
            <label
              data-checked={form.ackNoReferral}
              className="checkbox-row"
            >
              <input
                data-testid={POST_MODAL.checkNoReferral}
                type="checkbox"
                checked={form.ackNoReferral}
                onChange={(e) => set("ackNoReferral", e.target.checked)}
                className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
              />
              <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-charcoal)]">
                I understand that no referral fees are owed to the listing agent on leads created through the open house agent&rsquo;s activities *
              </span>
            </label>

            <label
              data-checked={form.ackMarketing}
              className="checkbox-row"
            >
              <input
                data-testid={POST_MODAL.checkMarketing}
                type="checkbox"
                checked={form.ackMarketing}
                onChange={(e) => set("ackMarketing", e.target.checked)}
                className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
              />
              <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-charcoal)]">
                I agree to allow the agent holding the home open to do marketing to drive traffic to the open house *
              </span>
            </label>

            <label
              data-checked={form.notifyOnReport}
              className="checkbox-row"
            >
              <input
                data-testid={POST_MODAL.checkNotifyReport}
                type="checkbox"
                checked={form.notifyOnReport}
                onChange={(e) => set("notifyOnReport", e.target.checked)}
                className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
              />
              <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-charcoal)]">
                Email me a copy of the self-report when an agent hosts an open house at this property
              </span>
            </label>
          </div>

          {/* Submit */}
          <div className="pt-2 pb-1 sticky bottom-0">
            <button
              data-testid={POST_MODAL.submitBtn}
              type="submit"
              disabled={!canSubmit || submitting}
              className={`w-full py-4 rounded-full text-base kw-btn-primary ${(!canSubmit || submitting) ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              Post Listing
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PostListingModal;
